import { AfterViewInit, ChangeDetectorRef, Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { GoalKpiEditComponent } from 'src/app/components';
import { GoalInformationDialogComponent } from 'src/app/dialogs/goal-information-dialog/goal-information-dialog.component';
import { GoalInformation } from 'src/app/enums/goal-information.enum';
import { GoalTypeEnum } from 'src/app/enums/goal-type.enum';
import { GoalPermissionsHelper } from 'src/app/helpers/goal-permissions.helper';
import { IResultModel, ResultModel } from 'src/app/models/general/result.model';
import { GoalPage } from 'src/app/models/goal-page.model';
import { GoalModel } from 'src/app/models/goal.model';
import { KpiModel } from 'src/app/models/kpi.model';
import { State } from 'src/app/models/state.model';
import { GoalService, StateService } from 'src/app/services';
import { ErrorMessageService } from 'src/app/shared/services';
import { DataTypeHelper } from 'src/app/shared/helpers/data-type.helper';

@UntilDestroy()
@Component({
  selector: 'app-numeric-goal',
  templateUrl: './numeric-goal.component.html',
  styleUrl: './numeric-goal.component.scss'
})
export class NumericGoalComponent implements OnInit, AfterViewInit {
  public loading = false;

  public mainForm: FormGroup;
  public data: GoalPage;

  public state: State;

  public get isSelf(): boolean {
    return this.state?.me.value?.employee?.id === this.data?.employeeId;
  }

  @ViewChildren(GoalKpiEditComponent) kpiComponents!: QueryList<GoalKpiEditComponent>;

  public get isAllChildValid(): boolean {
		if (!this.kpiComponents || this.kpiComponents.length === 0) return true;

    return this.kpiComponents.toArray().every(c => c.kpiForm.valid);
	}

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private goalService: GoalService,
    private msgService: ErrorMessageService,
    stateService: StateService,
    private cd: ChangeDetectorRef,
    private dialog: MatDialog
  ) {
    this.state = stateService.state;
  }

  ngOnInit() {
    this.mainForm = this.createForm();

    this.route.params.pipe(untilDestroyed(this)).subscribe((params) => {
      const goalId = params['goalId'] ? parseInt(params['goalId']) : null;
      const goalPeriodId = params['goalPeriodId'] ? parseInt(params['goalPeriodId']) : null;

      const obs: Observable<GoalPage> = goalId
      			? this.goalService.getGoal(goalId)
      			: this.goalService.getNewGoal(goalPeriodId, GoalTypeEnum.NumericGoal);

      if (!obs) {
				this.loading = false;
				return;
			}

      obs.pipe(untilDestroyed(this)).subscribe({
            next: (data: GoalPage) => {
              this.data = data;
              this.loading = false;
             
              this.mainForm.patchValue(this.data.goal);
              this.setFormControlState();
              this.cd.detectChanges(); // Trigger change detection
            },
            error: (e) => {
              this.loading = false;
            }
      });

    });
  }

  ngAfterViewInit() {
		this.kpiComponents.changes
			.pipe(untilDestroyed(this))
			.subscribe(() => {
				this.cd.detectChanges();
			});
	}

  private createForm(): FormGroup {
    return this.fb.group({
      id: 0,

      goalTextId: null,
      name: null,
      
      goalPeriodId: null,
      targetValue: null,
      goalType: null,
      result: null,
      weight: [null, [Validators.min(1), Validators.max(100), Validators.pattern(/^\d+$/)]]
    });
  }

  public setFormControlState() {
    if (this.state.isAdmin || this.state.isSuperAdmin) return;

    if (!GoalPermissionsHelper.canEditWeight(this.data?.goalPeriod, this.state)) {
			this.mainForm.get('weight')?.disable({ emitEvent: false });
		}

	}

  public isAllowEditForm(): boolean {
    return GoalPermissionsHelper.canGoalFormEdit(this.data.goalPeriod, this.state);
	}

  public allowAddNew(): boolean {
    return GoalPermissionsHelper.canGoalActionAddNew(this.data?.goalPeriod, this.state);
  }

  public allowOk(): boolean {
    if (this.mainForm.invalid) return false;
    return GoalPermissionsHelper.canGoalActionSave(this.data?.goalPeriod, this.state);
  }

  getBackLink(): string {
    return `/goals/${this.data.reviewPeriodId}/${this.data.employeeId}`;
  }

  //delete from the local array when id is null, not delete when id is set
  onKpiDelete(index: number): void {
      this.data.goal.kpiList.splice(index, 1);
      this.cd.detectChanges();
  }

  onNewKpi(): void {
    const kpi = new KpiModel();
    kpi.reviewPeriodId = this.data.reviewPeriodId;
    kpi.aboutEmployeeId = this.data.employeeId;
  
    this.data.goal.kpiList.push(kpi);
  }

  saveGoal(close: boolean) {
    const goal = this.mainForm.getRawValue() as GoalModel;
    goal.actions = [];
    goal.kpiList = [];

    for (const c of this.kpiComponents) {

      if (c.kpiForm.invalid)
      {
        c.kpiForm.markAllAsTouched();
        this.msgService.showSnackBarMessage('fixTheIncorrectFieldsForSave');
        return;
      }

      const kpiRaw = c.kpiForm.getRawValue();
      let kpi = kpiRaw as KpiModel;
      kpi.planned = DataTypeHelper.parseToNumber(kpiRaw.planned);
      kpi.achieved = DataTypeHelper.parseToNumber(kpiRaw.achieved);
      kpi.weight = DataTypeHelper.parseToNumber(kpiRaw.weight);
      kpi.minExpectation = DataTypeHelper.parseToNumber(kpiRaw.minExpectation);
      kpi.comments = c.kpi.comments;
      kpi.name = (kpi.name as any)?.text ? (kpi.name as any).text : kpi.name;
      goal.kpiList.push(kpi);
    }

    this.goalService.saveGoal(goal)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (result) => {
          this.loading = false;

          if (close)
            this.router.navigate([this.getBackLink()]);

          this.msgService.showSnackBarMessage('saveSuccessful'); 
        },
        error: (err: IResultModel<ResultModel>) => {
          this.loading = false;
          this.msgService.handleError<ResultModel>(err);
        }
      });
  }

  onOk(close: boolean = true): void {
    if (this.mainForm.invalid) {
      return;
    }

    this.loading = true;

    this.saveGoal(close);
  }

  showInformation(): void {
    this.dialog.open(GoalInformationDialogComponent, {
        width: "850px",
        data: { goalInfoType: GoalInformation.NumericGoals }
    });
  }

}
